<template>
  <!-- Shared video statuses -->
  <div
    ref="sharingsContainer"
    class="sharing-statuses"
    :style="sharingsContainerStyle"
  >
    <!-- Scroll panel arrow -->
    <div class="statuses-scroller" :style="scrollerStyle">
      <scroll-arrow
        v-if="canBackward"
        :width="6"
        :height="11"
        direction="left"
        @click="scrollIconsBackward"
      />
    </div>
    <!-- Status icons -->
    <div ref="sharings" class="sharing-status-icons " :style="scrollIconsStyle">
      <div>
        <sharing-status-icon
          v-if="isUploadedToMangomolo || isUploadingToMangomolo"
          :size="iconSize"
          social="mangomolo"
          :status="mangomoloLoadingStatus"
        />
        <sharing-status-icon
          v-for="share in sharings"
          :key="share.id"
          :size="iconSize"
          :social="share.social"
          :status="share.status"
          :link="share.link"
          :percentage="share.percentage"
          :publish-time="share.publishTime"
          :timezone="timezone"
          @removed="$emit('sharingRemoved', { id: share.id })"
        />
        <!-- padding div -->
        <div :style="{ 'min-width': scrollPadding + 'px' }" />
      </div>
    </div>
    <!-- Scroll panel arrow -->
    <div class="statuses-scroller" :style="scrollerStyle">
      <scroll-arrow
        v-if="canForward"
        :width="6"
        :height="11"
        direction="right"
        @click="scrollIconsForward"
      />
    </div>
  </div>
</template>

<script>
import SharingStatusIcon from "./SharingStatusIcon.vue";
import { SOCIAL_SHARING_STATUS } from "@/constants.js";
import { mapActions, mapState } from "vuex";

import ScrollArrow from "@/components/icons/ScrollArrow.vue";

import { getBounded } from "@/utils/calc.js";

import dayjs from "dayjs";

// @group Socials
// Sharing status icons scrolling panel
export default {
  name: "SharingStatusesScroller",
  components: {
    SharingStatusIcon,
    ScrollArrow
  },
  props: {
    /* Sharings metadata array */
    sharings: {
      type: Array,
      default: () => []
    },
    /** is video uploaded to main application */
    isUploadedToMangomolo: {
      type: Boolean,
      default: false
    },
    /** is video uploading to main application now */
    isUploadingToMangomolo: {
      type: Boolean,
      default: false
    },
    /** count of sharings in row */
    length: {
      type: Number,
      default: 3,
      validator(val) {
        return Number.isInteger(val) && val > 0;
      }
    },
    /** social icon width in pixels */
    iconSize: {
      type: Number,
      default: 26,
      validator(val) {
        return Number.isInteger(val) && val > 10;
      }
    },
    /** scroll padding width in pixels */
    scrollPadding: {
      type: Number,
      default: 3,
      validator(val) {
        return Number.isInteger(val) && val > 0;
      }
    },
    /** scroller width in pixels */
    scrollerWidth: {
      type: Number,
      default: 16,
      validator(val) {
        return Number.isInteger(val) && val > 10;
      }
    },
    // Sharing time timezone
    timezone: {
      type: String,
      default: dayjs.tz.guess()
    }
  },
  data() {
    return {
      startIconIndex: 0
    };
  },
  computed: {
    ...mapState({
      /** modals */
      modals: state => state.modals.clipper.MODALS,
      openedModal: state => state.modals.clipper.opened,
      modalData: state => state.modals.clipper.modalData,
      isStudioMode: state => state.services.isStudioMode,
    }),
    // @vuese
    // Start icon index getter/setter
    start: {
      get() {
        return this.startIconIndex;
      },
      set(newStart) {
        this.startIconIndex = getBounded(newStart, 0, this.sharings.length - 1);
      }
    },
    // @vuese
    // Width of all sharing icons in pixels
    scrollWidth() {
      return this.length * this.iconSize;
    },
    // @vuese
    // Is it's possible to scroll list to start
    canForward() {
      return this.start + this.length < this.sharings.length;
    },
    // @vuese
    // Is it's possible to scroll list to end
    canBackward() {
      return this.start > 0;
    },
    // @vuese
    // Scroll icons width and paddings CSS style object
    scrollIconsStyle() {
      return {
        width: this.scrollWidth + "px",
        "padding-left": this.scrollPadding + "px",
        "padding-right": this.scrollPadding + "px"
      };
    },
    // @vuese
    // Scroller width CSS style object
    scrollerStyle() {
      return {
        width: this.scrollerWidth + "px"
      };
    },
    // @vuese
    // Scroll container width CSS style object
    sharingsContainerStyle() {
      const fullScrollWidth = this.scrollPadding * 2 + this.scrollWidth;
      const width = this.scrollerWidth * 2 + fullScrollWidth;
      return {
        width: width + "px"
      };
    },
    mangomoloLoadingStatus() {
      if (this.isUploadingToMangomolo) {
        return SOCIAL_SHARING_STATUS.PROCESSING;
      }

      if (this.isUploadedToMangomolo) {
        return SOCIAL_SHARING_STATUS.READY;
      }

      return null;
    },
    lastSharing() {
      return this.sharings.at(-1).status;
    }
  },
  watch: {
    // @vuese
    // Scroll list
    start(newStart, oldStart) {
      const step = newStart - oldStart;
      this.$refs.sharings.scrollBy({
        left: step * this.iconSize,
        top: 0,
        behaviour: "smooth"
      });
    },
    sharings(newVal, oldVal) {
      if (this.isStudioMode) {
        if (oldVal === undefined) {
          return;
        }
        if (
          newVal.at(-1).status === "ready" &&
          newVal.at(-1).status != oldVal.at(-1).status
        ) {
          this.openClipperModal({
            modal: this.modals.MESSAGEMODAL,
            data: this.sharings.at(-1)
          });
        }
      }
    }
  },
  methods: {
    ...mapActions("modals", ["openClipperModal", "closeClipperModal"]),
    // @vuese
    // Scroll to left
    scrollIconsBackward() {
      this.start -= this.length;
    },
    // @vuese
    // Scroll to right
    scrollIconsForward() {
      this.start += this.length;
    }
  }
};
</script>

<style scoped>
.sharing-statuses {
  display: flex;
  justify-content: center;
  align-items: center;
}
.sharing-status-icons {
  overflow: hidden;
  scroll-behavior: smooth;

  padding-top: 2px;
  padding-bottom: 2px;

  background-color: black;
  border-radius: 5px;
}
.sharing-status-icons > div {
  display: flex;
}
.sharing-padding {
  padding-top: 3px;
}
.statuses-scroller {
  cursor: pointer;
}
.process-link-box {
  width: 20px;
}
.sharing-box {
  min-width: 20px;
  height: 20px;
  padding: 1px;
}
</style>
